export type VideoBackground = 'studio' | 'soft_blue' | 'warm_gray';

type SegmenterState = {
  imageSegmenter: {
    segmentForVideo: (
      video: HTMLVideoElement,
      timestampMs: number,
      callback: (result: {
        categoryMask?: {
          getAsUint8Array: () => Uint8Array;
        };
      }) => void,
    ) => void;
  };
  ready: boolean;
};

const BACKGROUNDS: Record<VideoBackground, CanvasGradient | string> = {
  studio: '#f7f8f9',
  soft_blue: '#e7f0f4',
  warm_gray: '#ebe7df',
};

const VENDOR_BASE = '/vendor/mediapipe';
const WASM_BASE = `${VENDOR_BASE}/wasm`;
const MODEL_PATH = `${VENDOR_BASE}/selfie_segmenter.tflite`;

let statePromise: Promise<SegmenterState | null> | null = null;

export function getBackgroundColor(background: VideoBackground): string {
  const value = BACKGROUNDS[background];
  return typeof value === 'string' ? value : '#f7f8f9';
}

export async function loadSelfieSegmenter(): Promise<SegmenterState | null> {
  if (statePromise) return statePromise;
  statePromise = (async () => {
    try {
      // Load MediaPipe from our own bundle so a CDN compromise can't replace
      // the inference code on a page that already has camera + mic permission.
      // The wasm and the .tflite model are vendored under /vendor/mediapipe.
      // The package is CJS; with esModuleInterop the dynamic import wraps it in
      // { default: ... }. Cast to the namespace type so we can use named exports.
      const vision = await import('@mediapipe/tasks-vision') as unknown as typeof import('@mediapipe/tasks-vision');
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE);
      const imageSegmenter = await vision.ImageSegmenter.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: MODEL_PATH,
          delegate: 'GPU',
        },
        runningMode: 'VIDEO',
        outputCategoryMask: true,
      });
      return { imageSegmenter, ready: true };
    } catch (err) {
      // Silently degrade — the recorder falls back to the raw camera feed
      // when segmentation is unavailable. Surfacing the error here would
      // block recording for users on browsers without GPU/wasm support.
      // eslint-disable-next-line no-console
      console.warn('Selfie segmenter failed to initialize', err);
      return null;
    }
  })();
  return statePromise;
}

export function drawRawVideo(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  background: VideoBackground,
) {
  ctx.fillStyle = getBackgroundColor(background);
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(video, 0, 0, width, height);
}

export function drawSegmentedVideo(
  ctx: CanvasRenderingContext2D,
  video: HTMLVideoElement,
  width: number,
  height: number,
  background: VideoBackground,
  segmenterState: SegmenterState | null,
) {
  if (!segmenterState?.ready) {
    drawRawVideo(ctx, video, width, height, background);
    return;
  }

  try {
    segmenterState.imageSegmenter.segmentForVideo(video, performance.now(), (result) => {
      ctx.save();
      ctx.fillStyle = getBackgroundColor(background);
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(video, 0, 0, width, height);
      const mask = result?.categoryMask;
      if (!mask) {
        ctx.restore();
        return;
      }
      const maskCanvas = document.createElement('canvas');
      maskCanvas.width = width;
      maskCanvas.height = height;
      const maskCtx = maskCanvas.getContext('2d');
      if (!maskCtx) {
        ctx.restore();
        return;
      }
      const imageData = maskCtx.createImageData(width, height);
      // selfie_segmenter's category mask is a uint8 where class 0 = person
      // (foreground) and class 1 = background. We want to KEEP the person
      // and replace the background, so the mask must be opaque on person
      // pixels (data[i] === 0) and transparent everywhere else.
      const data = mask.getAsUint8Array();
      for (let i = 0; i < width * height; i += 1) {
        const isForeground = data[i] === 0;
        const alpha = isForeground ? 255 : 0;
        imageData.data[i * 4] = 255;
        imageData.data[i * 4 + 1] = 255;
        imageData.data[i * 4 + 2] = 255;
        imageData.data[i * 4 + 3] = alpha;
      }
      maskCtx.putImageData(imageData, 0, 0);
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(maskCanvas, 0, 0, width, height);
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = getBackgroundColor(background);
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    });
  } catch {
    drawRawVideo(ctx, video, width, height, background);
  }
}
